/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type { ReactElement, ReactNode } from "react";
import {
  ErrorBoundary as ErrorBoundaryBase,
  type ErrorBoundaryProps,
} from "react-error-boundary";

import { HttpError, QueryErrorResetBoundary } from "@superweb/api";

import { useErrorBoundaryOptions } from "./error-boundary-context";
import { Unexpected } from "./unexpected";

export type ErrorFallbackProps = {
  reset: () => void;
  error: Error;
  traceId?: string;
};

export const ErrorBoundary = ({
  children,
  fallback,
  onError,
  resetKeys,
}: {
  children?: ReactNode;
  fallback?: (props: ErrorFallbackProps) => ReactElement | null;
  onError?: (error: Error) => void;
  resetKeys?: ErrorBoundaryProps["resetKeys"];
}) => {
  const { logError } = useErrorBoundaryOptions();

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundaryBase
          onError={(error, { componentStack }) => {
            const traceId =
              error instanceof HttpError
                ? error.headers.get("x-yatraceid") ?? undefined
                : undefined;

            logError(
              {
                message: error.message,
                additional: {
                  componentStack: componentStack ?? undefined,
                  traceId,
                },
              },
              error,
            );

            onError?.(error);
          }}
          onReset={reset}
          resetKeys={resetKeys}
          fallbackRender={({ resetErrorBoundary, error }) => {
            const traceId =
              error instanceof HttpError
                ? error.headers.get("x-yatraceid") ?? undefined
                : undefined;

            return fallback ? (
              fallback({
                reset: resetErrorBoundary,
                error: error as Error,
                traceId,
              })
            ) : (
              <Unexpected reset={resetErrorBoundary} traceId={traceId} />
            );
          }}
        >
          {children}
        </ErrorBoundaryBase>
      )}
    </QueryErrorResetBoundary>
  );
};
